import GoogleTrack from '@shared/utils/tracker/googleTrack';

import { isA1App } from '@shared/utils/platform/platformDetection';
import { LSK } from '@shared/utils/storage/config';
import { HttpDependency } from '@shared/utils/http/dependency';

export enum TrackEventName {
	Subscribe = 'Subscribe',
	SignUp = 'SignUp',
	SpentCredits = 'SpentCredits',
}

interface TrackingExtraInfo {
	paidCurrency: string;
	paidTotalAmount: number;
	transactionId: string;
}

// 基础事件映射，Facebook、Snapchat 和 TikTok 使用相同的事件名称
const baseEventMap = {
	[TrackEventName.Subscribe]: 'Subscribe',
	[TrackEventName.SignUp]: 'CompleteRegistration',
	[TrackEventName.SpentCredits]: 'SpentCredits',
} as const;

export const FacebookEvent = baseEventMap as Record<TrackEventName, string>;
export const SnapchatEvent = baseEventMap as Record<TrackEventName, string>;
export const TiktokEvent = baseEventMap as Record<TrackEventName, string>;

export const FirebaseEvent = <Record<TrackEventName, string>>{
	[TrackEventName.Subscribe]: 'purchase',
	[TrackEventName.SignUp]: 'sign_up',
	[TrackEventName.SpentCredits]: 'spend_virtual_currency',
};

/**
 * 执行事件追踪
 */
export const trackEvent = (event: TrackEventName, extraInfo?: TrackingExtraInfo) => {
	// 非App环境不执行
	if (!isA1App) return;
	trackFacebookEvent(FacebookEvent[event], extraInfo);
	trackSnapchatEvent(SnapchatEvent[event], extraInfo);
	trackFirebaseEvent(event, extraInfo);
};

// 回传firebase事件（Android端）
export const trackFirebaseEvent = (eventName: TrackEventName, extraInfo?: TrackingExtraInfo) => {
	const firebaseEventName = FirebaseEvent[eventName];
	let params = {};

	switch (eventName) {
		case TrackEventName.SignUp:
			// 注册事件: sign_up
			params = { method: 'email' };
			break;
		case TrackEventName.Subscribe:
			// 订阅事件: purchase
			if (extraInfo) {
				params = {
					currency: extraInfo.paidCurrency,
					value: extraInfo.paidTotalAmount,
					transaction_id: extraInfo.transactionId,
				};
			}
			break;
		case TrackEventName.SpentCredits:
			// 使用积分事件: spend_virtual_currency
			params = {
				virtual_currency_name: 'credits',
				value: 1,
			};
			break;
	}

	window.AndroidInterface?.trackFirebaseEvent?.(firebaseEventName, JSON.stringify(params));
};

// 回传facebook 事件
export const trackFacebookEvent = (facebookEvent: string, extraInfo?: TrackingExtraInfo) => {
	try {
		window.AndroidInterface?.trackFaceBookEvent?.(facebookEvent, JSON.stringify(extraInfo || {}));
	} catch (error) {
		console.log('用户app版本较低', error);
		window.AndroidInterface?.trackFaceBookEvent?.(facebookEvent);
	}
};

// 回传 Snapchat 事件
export const trackSnapchatEvent = (snapchatEvent: string, extraInfo?: TrackingExtraInfo) => {
	window.AndroidInterface?.trackSnapchatEvent?.(snapchatEvent, JSON.stringify(extraInfo || {}));
};

// 回传Tiktok 事件
export const trackTiktokEvent = (tiktokEvent: string, extraInfo?: TrackingExtraInfo) => {
	window.AndroidInterface?.trackTiktokEvent?.(tiktokEvent, JSON.stringify(extraInfo || {}));
};

/** 追踪注册转化（google、facebook） */
export function trackRegistrationConversion() {
	GoogleTrack.registerTrack();
	// 注册追踪
	trackEvent(TrackEventName.SignUp);
}

HttpDependency.inject({
	generateCallback() {
		// 获取是否上报过生图
		const isGenerateImageReported = localStorage.getItem(LSK.GENERATE_IMAGE_REPORTED);
		if (isGenerateImageReported) return;
		trackEvent(TrackEventName.SpentCredits);
		localStorage.setItem(LSK.GENERATE_IMAGE_REPORTED, 'true');
	},
});
